import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DRINK_PROFILE_LABELS,
  WINE_TYPE_LABELS,
  type ConsultaDeRotuloDTO,
  type LabelExtraction,
  type UploadedPhotoDTO,
} from '@adega/shared';
import { ErrorBox } from '../components/Feedback.js';
import { PageHeader } from '../components/PageHeader.js';
import { useAuth, useConsultarRotulo, useCotaDeConsultas, useUploadPhoto } from '../api/queries.js';
import { FORMULARIO_VAZIO, aplicarLeituraNoFormulario } from '../lib/formularioGarrafa.js';
import { chaveDoRascunho, fotosDaLeitura, gravarRascunho } from '../lib/rascunhoGarrafa.js';

/**
 * "Que vinho é este e vale levar?" — a pergunta da prateleira do mercado.
 *
 * **Não cadastra nada.** Fotografa, lê, responde. O desenho e o porquê estão em
 * `docs/decisoes.md`, "Consulta de rótulo: a pergunta do mercado".
 *
 * A pergunta aqui é diferente da de casa. Em casa: "quando abrir?". No mercado:
 * **"compro?"** — por isso o veredito vem antes dos detalhes, e os detalhes
 * ficam abaixo para quem quiser.
 */
export function ConsultarRotulo() {
  const navigate = useNavigate();
  const { data: auth } = useAuth();
  const { data: cota } = useCotaDeConsultas();
  const upload = useUploadPhoto();
  const consultar = useConsultarRotulo();

  const [foto, setFoto] = useState<UploadedPhotoDTO | null>(null);
  const [verso, setVerso] = useState<UploadedPhotoDTO | null>(null);
  const [resultado, setResultado] = useState<ConsultaDeRotuloDTO | null>(null);

  /*
    Dois inputs e dois botões, como no cadastro. Com `capture` o Android abre só
    a câmera; sem ele, o Android recente abre só o seletor de fotos, que não tem
    câmera. Nenhum atributo único cobre os dois casos.
  */
  const inputCamera = useRef<HTMLInputElement>(null);
  const inputGaleria = useRef<HTMLInputElement>(null);
  const inputVerso = useRef<HTMLInputElement>(null);

  const ocupado = upload.isPending || consultar.isPending;

  const receberFoto = async (input: HTMLInputElement, qual: 'frente' | 'verso') => {
    const arquivo = input.files?.[0];
    // Limpa já: sem isto, escolher a mesma foto duas vezes não dispara o evento.
    input.value = '';
    if (!arquivo) return;

    setResultado(null);
    const enviada = await upload.mutateAsync(arquivo);
    if (qual === 'frente') setFoto(enviada);
    else setVerso(enviada);
  };

  /**
   * A pergunta sai por toque, não assim que a foto sobe.
   *
   * Duas razões: a pessoa confere o enquadramento antes de gastar uma consulta,
   * e **o contrarrótulo só cabe aqui** — se a análise disparasse na primeira
   * foto, não haveria momento para oferecer o verso sem cobrar duas vezes pelo
   * mesmo vinho.
   */
  const perguntar = async () => {
    if (!foto) return;
    setResultado(
      await consultar.mutateAsync({ photoKey: foto.photoKey, backPhotoKey: verso?.photoKey ?? null }),
    );
  };

  /**
   * "Comprei" — a ponte entre curiosidade e coleção.
   *
   * Grava o rascunho que a tela de cadastro já sabe ler, em vez de inventar uma
   * passagem de estado própria. E grava `leituraConcluida`: a leitura já foi
   * paga aqui, e sem isso o cadastro pediria outra — que em produção, com duas
   * réplicas e memória por processo, teria boa chance de cobrar de novo.
   */
  const comprei = () => {
    if (!foto || !resultado || !auth?.user) return;

    const fotoSalva = {
      photoKey: foto.photoKey,
      thumbKey: foto.thumbKey,
      backPhotoKey: verso?.photoKey ?? null,
      url: foto.photoUrl,
    };

    gravarRascunho(chaveDoRascunho(auth.user.id), {
      form: aplicarLeituraNoFormulario(FORMULARIO_VAZIO, resultado.rotulo),
      camposEditados: [],
      fotoSalva,
      confianca: resultado.rotulo.confidence,
      cellarId: null,
      vaga: null,
      leituraConcluida: fotosDaLeitura(fotoSalva),
      leituraPendente: null,
    });

    navigate('/adicionar');
  };

  const erro = upload.error ?? consultar.error;

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Só quero saber"
        subtitulo="Nada aqui vai para a sua adega"
        voltar="/novo"
      />

      <input
        ref={inputCamera}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void receberFoto(e.currentTarget, 'frente')}
      />
      <input
        ref={inputGaleria}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void receberFoto(e.currentTarget, 'frente')}
      />
      <input
        ref={inputVerso}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void receberFoto(e.currentTarget, 'verso')}
      />

      {erro ? <ErrorBox erro={erro} /> : null}

      {ocupado ? (
        <p className="card py-6 text-center text-sm text-adega-muted">
          {upload.isPending ? 'Enviando a foto…' : 'Lendo o rótulo… pode levar alguns segundos.'}
        </p>
      ) : null}

      {!resultado && !ocupado && !foto ? (
        <section className="card space-y-3">
          <p className="text-sm leading-relaxed text-adega-muted">
            Fotografe o rótulo e eu digo se é para abrir agora ou guardar — e se você já tem algo
            parecido em casa.
          </p>

          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => inputCamera.current?.click()}
          >
            Fotografar o rótulo
          </button>
          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => inputGaleria.current?.click()}
          >
            Escolher da galeria
          </button>

          {/* A cota aparece ANTES de gastar: cota que só surge no erro
              surpreende na pior hora — na loja, com a garrafa na mão. */}
          {cota ? (
            <p className="pt-1 text-center text-xs text-adega-muted">
              {cota.restantes > 0
                ? `Restam ${cota.restantes} de ${cota.limite} consultas este mês.`
                : `Você já usou as ${cota.limite} consultas deste mês. A conta vira no dia 1º.`}
            </p>
          ) : null}
        </section>
      ) : null}

      {/*
        Foto da frente pronta, pergunta ainda não feita.

        É aqui que o contrarrótulo cabe — e ele importa mais no mercado que no
        cadastro: a garrafa está na mão, virar custa dois segundos, e é no verso
        que estão a safra e a ficha do produtor, que é o que mais afina a
        estimativa de guarda. Perguntar sem oferecer seria desperdiçar a consulta
        com menos informação do que havia disponível.
      */}
      {!resultado && !ocupado && foto ? (
        <section className="card space-y-3">
          <div className="flex gap-3">
            <img
              src={foto.photoUrl}
              alt="Foto do rótulo"
              className="h-28 w-20 shrink-0 rounded-lg object-cover"
            />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm">
                Rótulo fotografado{verso ? ' e contrarrótulo também' : ''}.
              </p>
              <button
                type="button"
                className="btn-ghost px-0 text-xs"
                onClick={() => inputCamera.current?.click()}
              >
                Trocar a foto
              </button>
            </div>
          </div>

          {!verso ? (
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => inputVerso.current?.click()}
            >
              Fotografar o contrarrótulo
            </button>
          ) : null}

          {!verso ? (
            <p className="text-xs leading-relaxed text-adega-muted">
              Opcional, mas ajuda: é no verso que costumam estar a safra e o que o produtor declara
              sobre o vinho.
            </p>
          ) : null}

          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => void perguntar()}
            disabled={cota?.restantes === 0}
          >
            {cota?.restantes === 0 ? 'Sem consultas este mês' : 'Perguntar'}
          </button>

          {cota && cota.restantes > 0 ? (
            <p className="text-center text-xs text-adega-muted">
              Vai usar 1 das {cota.restantes} consultas que restam.
            </p>
          ) : null}
        </section>
      ) : null}

      {resultado ? <Resposta consulta={resultado} aoComprar={comprei} /> : null}
    </div>
  );
}

function Resposta({
  consulta,
  aoComprar,
}: {
  consulta: ConsultaDeRotuloDTO;
  aoComprar: () => void;
}) {
  const { rotulo, jaTenho, cota } = consulta;
  const nome = [rotulo.producer, rotulo.name].filter(Boolean).join(' ') || 'Vinho sem nome no rótulo';
  const origem = [rotulo.region, rotulo.country].filter(Boolean).join(', ');
  // Tipo, uva e teor: o "que vinho é este" em uma linha. Cada parte só se o
  // rótulo trouxe — campo vazio some, nunca vira "não informado".
  const perfil = [
    rotulo.type ? WINE_TYPE_LABELS[rotulo.type] : null,
    rotulo.grapeVariety,
    rotulo.alcoholPercent ? `${rotulo.alcoholPercent.toLocaleString('pt-BR')}% vol.` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-3">
      <section className="card space-y-1">
        <h2 className="text-lg font-semibold leading-tight">{nome}</h2>
        <p className="text-sm text-adega-muted">
          {[rotulo.vintage ? `Safra ${rotulo.vintage}` : null, origem].filter(Boolean).join(' · ')}
        </p>
        {perfil ? <p className="text-sm text-adega-muted">{perfil}</p> : null}
      </section>

      {/* Logo depois da identidade: na loja, é o que mais pesa na decisão de
          compra depois do veredito. Só o que a leitura viu na garrafa — a IA
          não completa de memória (ver "Premiações" em docs/decisoes.md). */}
      {rotulo.premiacoes ? (
        <section className="card space-y-2 border-adega-gold/40">
          <h3 className="label text-adega-gold">Premiações na garrafa</h3>
          <ul className="space-y-1">
            {rotulo.premiacoes.split(' · ').map((premio) => (
              <li key={premio} className="flex gap-2 text-sm leading-relaxed">
                <span aria-hidden="true" className="text-adega-gold">
                  ★
                </span>
                <span>{premio}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* O veredito antes dos detalhes: na loja a pergunta é "compro?", e não
          "quando abrir?". */}
      <section className="card space-y-3 border-adega-wine-light/60">
        {consulta.drinkProfile ? (
          <span className="inline-block rounded-full bg-adega-gold px-3 py-1 text-xs font-bold uppercase tracking-wide text-adega-bg">
            {DRINK_PROFILE_LABELS[consulta.drinkProfile]}
          </span>
        ) : null}

        {consulta.drinkNote ? (
          <p className="text-[15px] leading-relaxed">{consulta.drinkNote}</p>
        ) : null}

        <p className="text-sm text-adega-muted">
          {consulta.drinkUntil
            ? `Beber até ${consulta.drinkUntil}${consulta.drinkPeak ? `, com o melhor momento em ${consulta.drinkPeak}` : ''}.`
            : 'Sem janela de guarda estimada para este vinho.'}
        </p>
      </section>

      {/* A linha que só este app consegue dizer. Some quando não há o que
          afirmar — quem tem zero garrafas vê a consulta inteira sem ela. */}
      {jaTenho ? (
        <section className="card border-adega-gold/40">
          <p className="text-sm leading-relaxed">
            <strong className="font-semibold">
              Você já tem {jaTenho.total} {jaTenho.uva}
            </strong>{' '}
            em casa
            {jaTenho.doMesmoProdutor > 0
              ? `, ${jaTenho.doMesmoProdutor === 1 ? 'uma delas' : `${jaTenho.doMesmoProdutor} delas`} deste mesmo produtor`
              : ''}
            .
          </p>
        </section>
      ) : null}

      <NoRotulo rotulo={rotulo} fotografouVerso={consulta.backPhotoKey !== null} />

      {consulta.agingGuidance ? (
        <section className="card space-y-2">
          <h3 className="label">Sobre a guarda</h3>
          <p className="text-sm leading-relaxed">{consulta.agingGuidance}</p>
        </section>
      ) : null}

      {consulta.pairingNotes ? (
        <section className="card space-y-2">
          <h3 className="label">Harmonização</h3>
          <p className="text-sm leading-relaxed">{consulta.pairingNotes}</p>
        </section>
      ) : null}

      <div className="space-y-2">
        <button type="button" className="btn-primary w-full" onClick={aoComprar}>
          Comprei — adicionar à minha adega
        </button>
        <p className="text-center text-xs text-adega-muted">
          Restam {cota.restantes} de {cota.limite} consultas este mês.
        </p>
      </div>
    </div>
  );
}

/** Para comparar textos: sem acento, sem caixa, espaço único. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Tira de "Do rótulo" o que outro campo da ficha já diz.
 *
 * "Do rótulo" é o campo de sobra da leitura, e a IA às vezes repete nele o que
 * também mandou para um campo específico: no primeiro teste real (25/10/2026),
 * "12 months oak barrel" apareceu em "Do rótulo" E em "Vinificação". Os dois
 * campos vêm de lugares diferentes da resposta, então a repetição não é bug de
 * tela -- é a mesma informação servindo a duas descrições.
 *
 * Resolver aqui, e não no prompt, porque prompt é probabilístico: diminuiria a
 * chance, não garantiria. Aqui é regra. O campo genérico cede para o específico,
 * e trecho a trecho (os itens vêm separados por " · "): "Vinhedo X · 12 months
 * oak barrel" continua mostrando o vinhedo, e só o estágio sai, porque já está
 * em Vinificação.
 */
function semRepetir(detalhes: string | null, outros: Array<string | null | undefined>): string | null {
  if (!detalhes) return null;
  const jaDitos = outros.filter((t): t is string => Boolean(t)).map(normalizar);
  const restantes = detalhes
    .split(' · ')
    .map((trecho) => trecho.trim())
    .filter((trecho) => {
      const n = normalizar(trecho);
      return n !== '' && !jaDitos.some((dito) => dito.includes(n));
    });
  return restantes.length > 0 ? restantes.join(' · ') : null;
}

/**
 * O que está escrito na garrafa: a parte mais confiável da resposta.
 *
 * A análise de guarda é palpite bem informado da IA; isto é o que o produtor
 * declarou. Já foi lido e pago com a consulta — até 25/10 era descartado aqui.
 * Desenho em `docs/decisoes.md`, "A ficha completa do vinho na resposta".
 *
 * O texto vem como a leitura transcreveu, sem a IA reescrever. Guarda e
 * harmonização do produtor ficam separadas das da IA, cada uma com o seu nome:
 * as duas podem discordar, e quem decide é a pessoa.
 */
function NoRotulo({
  rotulo,
  fotografouVerso,
}: {
  rotulo: LabelExtraction;
  fotografouVerso: boolean;
}) {
  const ficha = rotulo.producerSheet;
  const producao = rotulo.productionSize?.toLocaleString('pt-BR');
  const numeracao = rotulo.bottleNumber
    ? `Garrafa numerada · nº ${rotulo.bottleNumber}${producao ? ` de ${producao}` : ''}`
    : producao
      ? `Produção limitada a ${producao} garrafas`
      : null;

  // Primeiro o "o que é este vinho" (notas e história), depois o "como foi
  // feito", e por último o que o produtor indica para guardar e servir.
  const itens: Array<{ titulo: string; texto: string | null }> = [
    { titulo: 'Notas do produtor', texto: ficha?.tastingNotes ?? null },
    { titulo: 'A história', texto: ficha?.story ?? null },
    {
      titulo: 'Do rótulo',
      texto: semRepetir(rotulo.labelDetails, [
        ficha?.tastingNotes,
        ficha?.story,
        numeracao,
        ficha?.winemaking,
        ficha?.agingPotential,
        ficha?.servingTemperature,
        ficha?.pairing,
      ]),
    },
    { titulo: 'Produção', texto: numeracao },
    { titulo: 'Vinificação', texto: ficha?.winemaking ?? null },
    { titulo: 'O produtor indica guardar', texto: ficha?.agingPotential ?? null },
    { titulo: 'O produtor indica servir', texto: ficha?.servingTemperature ?? null },
    { titulo: 'O produtor harmoniza com', texto: ficha?.pairing ?? null },
  ];
  const presentes = itens.filter((item): item is { titulo: string; texto: string } => Boolean(item.texto));

  if (presentes.length === 0) {
    // Só a frente fotografada: é no verso que está quase toda a ficha, então
    // vale dizer onde ela está. Com o verso fotografado e nada escrito nele,
    // não há o que pedir — o bloco simplesmente não aparece.
    return fotografouVerso ? null : (
      <p className="px-1 text-xs text-adega-muted">
        Fotografe o contrarrótulo para ver o que o produtor diz sobre este vinho.
      </p>
    );
  }

  return (
    <section className="card space-y-3">
      <h3 className="label">No rótulo</h3>
      <dl className="space-y-3">
        {presentes.map((item) => (
          <div key={item.titulo} className="space-y-0.5">
            <dt className="text-xs font-semibold text-adega-muted">{item.titulo}</dt>
            <dd className="text-sm leading-relaxed">{item.texto}</dd>
          </div>
        ))}
      </dl>
      {!fotografouVerso ? (
        <p className="text-xs text-adega-muted">
          O contrarrótulo costuma trazer mais: vale fotografar o verso na próxima.
        </p>
      ) : null}
    </section>
  );
}
